// =============================================================================
// Template Repository - V3-2
// CRUD pour les modèles de tâches
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// Types
export interface TaskTemplatePayload {
  title: string;
  description?: string;
  priority?: string;
  complexity?: string;
  estimated_hours?: number;
  tags?: string[];
}

export interface TaskTemplate {
  id: string;
  org_id: string;
  project_id: string | null;
  name: string;
  description: string | null;
  payload: TaskTemplatePayload;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateInput {
  org_id: string;
  project_id?: string | null;
  name: string;
  description?: string;
  payload: TaskTemplatePayload;
  created_by?: string;
}

// =============================================================================
// Fetch Templates
// =============================================================================

export async function fetchTemplates(
  orgId: string,
  projectId?: string | null
): Promise<TaskTemplate[]> {
  let query = supabase
    .from('project_task_templates')
    .select('*')
    .eq('org_id', orgId)
    .order('name', { ascending: true });

  // Fetch both org-wide and project-specific templates
  if (projectId) {
    query = query.or(`project_id.is.null,project_id.eq.${projectId}`);
  } else {
    query = query.is('project_id', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[templateRepository] fetchTemplates error:', error);
    throw error;
  }

  return (data || []).map(t => ({
    ...t,
    payload: (t.payload || { title: '' }) as unknown as TaskTemplatePayload,
  }));
}

// =============================================================================
// Create Template
// =============================================================================

export async function createTemplate(input: CreateTemplateInput): Promise<TaskTemplate> {
  const { data, error } = await supabase
    .from('project_task_templates')
    .insert({
      org_id: input.org_id,
      project_id: input.project_id || null,
      name: input.name,
      description: input.description || null,
      payload: input.payload as unknown as Json,
      created_by: input.created_by || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[templateRepository] createTemplate error:', error);
    throw error;
  }

  return {
    ...data,
    payload: (data.payload || { title: '' }) as unknown as TaskTemplatePayload,
  };
}

// =============================================================================
// Update Template
// =============================================================================

export async function updateTemplate(
  templateId: string,
  updates: Partial<Pick<TaskTemplate, 'name' | 'description' | 'payload'>>
): Promise<TaskTemplate> {
  const updateData: Record<string, unknown> = {};
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.payload !== undefined) updateData.payload = updates.payload as unknown as Json;

  const { data, error } = await supabase
    .from('project_task_templates')
    .update(updateData as any)
    .eq('id', templateId)
    .select()
    .single();

  if (error) {
    console.error('[templateRepository] updateTemplate error:', error);
    throw error;
  }

  return {
    ...data,
    payload: (data.payload || { title: '' }) as unknown as TaskTemplatePayload,
  };
}

// =============================================================================
// Delete Template
// =============================================================================

export async function deleteTemplate(templateId: string): Promise<void> {
  const { error } = await supabase
    .from('project_task_templates')
    .delete()
    .eq('id', templateId);

  if (error) {
    console.error('[templateRepository] deleteTemplate error:', error);
    throw error;
  }
}
